/**
 * Client-side XSS Protection Utilities
 *
 * Provides sanitization functions for rendering user-supplied content safely.
 * Uses DOMPurify for HTML sanitization and includes URL validation.
 */

import DOMPurify from 'dompurify';

// Configure DOMPurify for strict sanitization
const purifyConfig = {
  ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br', 'span', 'ul', 'ol', 'li'],
  ALLOWED_ATTR: ['class'],
  ALLOW_DATA_ATTR: false,
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'svg', 'math'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'style'],
  RETURN_TRUSTED_TYPE: false,
} as const;

/**
 * Sanitizes HTML content using DOMPurify with strict configuration.
 * Use this when you need to render user-supplied HTML content.
 *
 * @param dirty - The potentially unsafe HTML string
 * @returns Sanitized HTML string safe for rendering
 */
export function sanitizeHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, purifyConfig) as string;
}

/**
 * Strips all HTML tags from a string, returning plain text.
 * Use this when you only want text content, not HTML.
 *
 * @param dirty - The potentially unsafe string
 * @returns Plain text with all HTML removed
 */
export function stripHtml(dirty: string | null | undefined): string {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, { ALLOWED_TAGS: [], RETURN_TRUSTED_TYPE: false }) as string;
}

/**
 * Dangerous URL schemes that should be blocked
 */
const DANGEROUS_URL_SCHEMES = [
  'javascript:',
  'data:',
  'vbscript:',
  'file:',
];

/**
 * Allowed URL schemes for links
 */
const ALLOWED_URL_SCHEMES = [
  'http:',
  'https:',
  'mailto:',
  'tel:',
];

/**
 * Validates and sanitizes a URL to prevent XSS attacks via dangerous schemes.
 *
 * @param url - The URL to validate
 * @returns The sanitized URL or empty string if invalid/dangerous
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return '';

  const trimmed = url.trim().toLowerCase();

  // Check for dangerous schemes
  for (const scheme of DANGEROUS_URL_SCHEMES) {
    if (trimmed.startsWith(scheme)) {
      console.warn(`Blocked dangerous URL scheme: ${scheme}`);
      return '';
    }
  }

  // Allow relative URLs
  if (trimmed.startsWith('/') || trimmed.startsWith('#') || trimmed.startsWith('.')) {
    return url.trim();
  }

  // Check for allowed schemes
  const hasAllowedScheme = ALLOWED_URL_SCHEMES.some(scheme =>
    trimmed.startsWith(scheme)
  );

  if (!hasAllowedScheme) {
    // If no scheme, assume it's a relative path or add https://
    if (!trimmed.includes(':')) {
      return url.trim();
    }
    console.warn(`Blocked URL with unrecognized scheme: ${url}`);
    return '';
  }

  return url.trim();
}

/**
 * Creates a safe href for anchor tags.
 * Returns '#' if the URL is invalid or dangerous.
 *
 * @param url - The URL to validate
 * @returns Safe URL or '#' if invalid
 */
export function safeHref(url: string | null | undefined): string {
  const sanitized = sanitizeUrl(url);
  return sanitized || '#';
}

/**
 * Escapes special HTML characters in a string.
 * Use this when inserting text into HTML without using React's JSX.
 *
 * @param text - The text to escape
 * @returns Escaped text safe for HTML insertion
 */
export function escapeHtml(text: string | null | undefined): string {
  if (!text) return '';

  const htmlEscapes: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
    '`': '&#x60;',
    '=': '&#x3D;',
  };

  return text.replace(/[&<>"'`=/]/g, char => htmlEscapes[char] || char);
}

/**
 * Validates that a string contains only alphanumeric characters and safe punctuation.
 * Use this for validating IDs, tokens, or other constrained inputs.
 *
 * @param value - The value to validate
 * @param allowedChars - Additional allowed characters (default: hyphen, underscore)
 * @returns True if valid, false otherwise
 */
export function isAlphanumericSafe(
  value: string | null | undefined,
  allowedChars: string = '-_'
): boolean {
  if (!value) return false;
  const pattern = new RegExp(`^[a-zA-Z0-9${escapeRegex(allowedChars)}]+$`);
  return pattern.test(value);
}

/**
 * Escapes special regex characters in a string.
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Sanitizes user input for display in text fields.
 * Removes control characters and limits length.
 *
 * @param input - The user input to sanitize
 * @param maxLength - Maximum allowed length (default: 1000)
 * @returns Sanitized string
 */
export function sanitizeInput(
  input: string | null | undefined,
  maxLength: number = 1000
): string {
  if (!input) return '';

  // Remove null bytes and most control characters (keep newlines and tabs)
  let sanitized = input
    .replace(/\0/g, '')
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');

  // Trim and limit length
  sanitized = sanitized.trim();
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
}

/**
 * Type guard to check if a value is a non-empty string
 */
export function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}
